import React from 'react';
import { Alert, Button, Offcanvas, Spinner } from 'react-bootstrap';
import { ArrowLeft, Plus } from 'lucide-react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../firebase';
import { useAssistantChatList } from '../../hooks/assistant/useAssistantChats';
import { useAssistantPanel } from '../../hooks/assistant/useAssistantPanel';
import { useIsSmallScreen } from '../../hooks/useIsSmallScreen';
import { AssistantRefLink } from './AssistantAnswer';
import AssistantConversation from './AssistantConversation';
import './Assistant.css';

interface Props {
    show: boolean;
    onHide: () => void;
}

/**
 * Byggnadsassistent: a global drawer for superadmins (spec: workspace docs/specs/ai-assistant.md).
 * The page stays usable beside it on desktop; on phones it covers the page and closes when a
 * link in an answer is followed.
 */
const AssistantPanel: React.FC<Props> = ({ show, onHide }) => {
    const [user] = useAuthState(auth);
    const isSmallScreen = useIsSmallScreen();
    const panel = useAssistantPanel();
    const list = useAssistantChatList(show ? user?.uid : undefined);
    const onNavigate = isSmallScreen ? onHide : () => undefined;

    return (
        <Offcanvas
            show={show}
            onHide={onHide}
            placement="end"
            backdrop={false}
            scroll
            className="assistant-panel"
            aria-label="Byggnadsassistent"
        >
            <Offcanvas.Header closeButton closeLabel="Stäng assistenten">
                {panel.chatKey ? (
                    <button type="button" className="assistant-back" onClick={panel.showList}>
                        <ArrowLeft size={18} /> Alla chattar
                    </button>
                ) : (
                    <Offcanvas.Title>Byggnadsassistent</Offcanvas.Title>
                )}
            </Offcanvas.Header>
            <Offcanvas.Body className="assistant-body">
                {panel.chatKey ? (
                    <AssistantConversation
                        // a new conversation starts with fresh local state
                        key={panel.chatKey}
                        chatKey={panel.chatKey}
                        contextBuildingKey={panel.contextBuildingKey}
                        sending={panel.sending}
                        error={panel.error}
                        onAsk={panel.ask}
                        onFeedback={panel.submitFeedback}
                        onNavigate={onNavigate}
                    />
                ) : (
                    <div className="assistant-chat-list">
                        <Button className="assistant-new-chat" onClick={panel.startNewChat}>
                            <Plus size={18} /> Ny chatt
                        </Button>
                        {list.status === 'loading' && <div className="assistant-list-status"><Spinner animation="border" size="sm" /> Laddar chattar…</div>}
                        {list.status === 'error' && <Alert variant="danger">Chattarna kunde inte läsas.</Alert>}
                        {list.status === 'loaded' && list.value.length === 0 && (
                            <div className="assistant-list-status">Inga tidigare chattar.</div>
                        )}
                        {list.status === 'loaded' && list.value.length > 0 && (
                            <ul>
                                {list.value.map((item) => (
                                    <li key={item.key}>
                                        <button type="button" className="assistant-chat-item" onClick={() => panel.openChat(item.key)}>
                                            <span className="assistant-chat-title">{item.title}</span>
                                            <span className="assistant-chat-meta">
                                                {item.updatedAt.slice(0, 16)}
                                                {item.contextBuildingKey && (
                                                    <>
                                                        {' · '}
                                                        <BuildingName buildingKey={item.contextBuildingKey} />
                                                    </>
                                                )}
                                            </span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </Offcanvas.Body>
        </Offcanvas>
    );
};

/** Building name only (no link: the row itself is a button). */
const BuildingName: React.FC<{ buildingKey: string }> = ({ buildingKey }) => (
    <AssistantRefLink assistantRef={{ kind: 'building', keys: [buildingKey], raw: `{building:${buildingKey}}` }} plain />
);

export default AssistantPanel;
